'use client'

import { Fragment, useRef, useState, useEffect } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { ArrowRight, Play, Calendar, CheckCircle } from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { useScrollProgress } from '@/hooks/useInView'
import { AttributedCtaIframe } from '@/components/conversion/AttributedCtaIframe'

/* ── Lazy-loaded 3D scene (desktop only, doesn't block LCP) ─────── */
const Scene3D = dynamic(
  () => import('@/components/3d/Scene3D').then(m => ({ default: m.Scene3D })),
  { ssr: false }
)
const FloatingRoof = dynamic(
  () => import('@/components/3d/FloatingRoof').then(m => ({ default: m.FloatingRoof })),
  { ssr: false }
)

/* ── Staggered word animation (CSS-driven, LCP-safe) ──────── */
function SplitText({
  text,
  highlight,
  className = '',
  delayStart = 0,
}: {
  text: string
  highlight?: string
  className?: string
  delayStart?: number
}) {
  const words = text.split(' ').filter(Boolean)

  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => {
        const isHighlight = highlight && word === highlight
        const delay = delayStart + i * 0.07 // stagger each word by 70ms
        return (
          <Fragment key={`${word}-${i}`}>
            <span
              className="hero-word-anim"
              style={{ animationDelay: `${delay}s` }}
            >
              {isHighlight ? (
                <span className="gradient-text-red">{word}</span>
              ) : (
                word
              )}
            </span>
            {/* A real space keeps the heading readable as text ("The Best Roofing CRM"), not just visually. */}
            {' '}
          </Fragment>
        )
      })}
    </span>
  )
}

/* ── Magnetic Button (inline for hero to avoid circular deps) ── */
function HeroMagneticWrap({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  // Cache rect to avoid repeated getBoundingClientRect() on every mousemove
  const cachedRect = useRef<DOMRect | null>(null)

  function handleMouse(e: React.MouseEvent) {
    // Use cached rect; only call getBoundingClientRect once when button mounts
    if (!cachedRect.current && ref.current) {
      cachedRect.current = ref.current.getBoundingClientRect()
    }
    const rect = cachedRect.current
    if (!rect) return
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    setOffset({
      x: (e.clientX - cx) * 0.25,
      y: (e.clientY - cy) * 0.25,
    })
  }

  function handleLeave() {
    setOffset({ x: 0, y: 0 })
    cachedRect.current = null // invalidate on leave so it re-measures if scrolled
  }

  return (
    <div
      ref={ref}
      style={{
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        transition: 'transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      className={className}
    >
      {children}
    </div>
  )
}

/* ── Trust badges ─────────────────────────────────────────── */
const TRUST_ITEMS = [
  'Unlimited users on every plan',
  'No contracts, cancel anytime',
  'Made for how roofing actually runs',
]

/* ── Main Hero ────────────────────────────────────────────── */
export function HeroSection() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    setMounted(true)
    const mql = window.matchMedia('(min-width: 768px)')
    setIsDesktop(mql.matches)
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  /* ── Scroll-driven parallax ──────────────────────────────── */
  const progress = useScrollProgress(containerRef)

  // Map scroll progress to parallax values
  const heroOpacity = Math.max(0, 1 - progress * 2.5)    // fades out by ~40% scroll
  const heroScale = 1 - progress * 0.08                   // shrinks slightly
  const heroY = progress * -60                             // moves up

  return (
    <>
      <section
        ref={containerRef}
        className="relative overflow-hidden min-h-[90vh] md:min-h-screen"
        id="hero"
      >
        {/* ── Background: gradient mesh + 3D floating roof ─── */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 gradient-mesh-hero noise-overlay" />

          {/* Radial glow behind hero */}
          <div className="absolute top-[20%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[900px] md:h-[900px] rounded-full bg-red-600/[0.06] blur-[120px] pointer-events-none" />

          {/* 3D Floating Roof — lazy-loaded, desktop only */}
          {mounted && isDesktop && (
            <div className="absolute inset-0 opacity-60">
              <Scene3D
                className="w-full h-full"
                camera={{ position: [0, 1.5, 6], fov: 45 }}
                autoRotate
                fallback={null}
              >
                <FloatingRoof scale={1.2} floatAmplitude={0.2} floatPeriod={5} />
              </Scene3D>
            </div>
          )}

          {/* Bottom fade to black */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none z-10" />
        </div>

        {/* ── Hero Content ─────────────────────────────────── */}
        <div
          style={{
            opacity: heroOpacity,
            transform: `scale(${heroScale}) translateY(${heroY}px)`,
          }}
          className="relative z-10 text-center px-5 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-24 sm:pt-28 md:pt-36 pb-6"
        >
          {/* Animated Badge */}
          <div
            className={`anim-scale-in anim-delay-1 ${mounted ? 'in-view' : ''} inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-800/80 bg-zinc-900/60 backdrop-blur-md mb-8`}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
            </span>
            <span className="text-xs sm:text-sm text-zinc-400 font-medium">
              Now live · Unlimited users · No contracts
            </span>
          </div>

          {/* Headline with staggered word animation (LCP-safe: opacity stays 1) */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] font-extrabold leading-[1.1] tracking-[-0.03em] mb-6 sm:mb-8">
            <SplitText
              text="The Best Roofing CRM —"
              delayStart={0.1}
            />
            <br className="hidden sm:block" />
            <span
              className="hero-word-anim gradient-text-red"
              style={{ animationDelay: '0.4s' }}
            >
              Run Leads, Jobs, and Crews
            </span>{' '}
            <br className="hidden lg:block" />
            <SplitText text=" In One System." delayStart={0.55} />
          </h1>

          {/* Subheadline (LCP-safe animation — opacity stays 1) */}
          <p
            className="hero-subtitle-anim text-base sm:text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Built for the Way Roofing Actually Works. The all-in-one software
            that replaces your CRM, estimating, scheduling, and invoicing tools
            — powered by AI.
          </p>

          {/* CTAs with magnetic effect */}
          <div
            className={`anim-fade-in-up ${mounted ? 'in-view' : ''} flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4`}
            style={{ transitionDelay: '0.9s' }}
          >
            <HeroMagneticWrap>
              <button
                onClick={() => setDemoModalOpen(true)}
                className="btn-primary text-base px-7 py-3.5 sm:px-8 sm:py-4 glow-red-sm border-glow-animated w-full sm:w-auto justify-center group"
              >
                <Calendar size={18} />
                <span>Book a Demo</span>
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </HeroMagneticWrap>
            <HeroMagneticWrap>
              <Link
                href="/pricing"
                className="btn-ghost text-base px-7 py-3.5 sm:px-8 sm:py-4 w-full sm:w-auto justify-center group"
              >
                <Play size={16} />
                <span>See Plans &amp; Pricing</span>
              </Link>
            </HeroMagneticWrap>
          </div>

          {/* Trust badges */}
          <div
            className={`anim-fade-in ${mounted ? 'in-view' : ''} flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8`}
            style={{ transitionDelay: '1.2s' }}
          >
            {TRUST_ITEMS.map((item, i) => (
              <span
                key={item}
                className={`anim-fade-in-up ${mounted ? 'in-view' : ''} flex items-center gap-1.5 text-xs sm:text-sm text-zinc-500`}
                style={{ transitionDelay: `${1.3 + i * 0.1}s` }}
              >
                <CheckCircle size={14} className="text-red-600/70" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Booking Modal */}
      <DemoModal open={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </>
  )
}

/* ── Demo Booking Modal ───────────────────────────────────── */
function DemoModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [iframeLoaded, setIframeLoaded] = useState(false)

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
      setIframeLoaded(false)
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <div
      className={`anim-overlay ${open ? 'visible' : ''} fixed inset-0 z-[100] flex items-center justify-center p-4`}
      role="dialog"
      aria-modal="true"
      aria-label="Book a Demo"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/85 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-full max-w-2xl glass-card-premium overflow-hidden z-10 transition-all duration-300 ${
          open
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-5'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800/50">
          <div>
            <h2 className="text-lg font-bold text-white">
              Book a Demo
            </h2>
            <p className="text-sm text-zinc-400">
              30-min live walkthrough with our team
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Iframe */}
        <div className="relative" style={{ minHeight: 'min(600px, 70vh)' }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-950">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-zinc-400">
                  Loading calendar...
                </span>
              </div>
            </div>
          )}
          {open && (
            <AttributedCtaIframe
              src={BOOK_A_DEMO_URL}
              page="home"
              cta="Hero — Book a Demo"
              className="w-full border-0"
              style={{ height: 'min(600px, 70vh)' }}
              onLoad={() => setIframeLoaded(true)}
              title="Book a Demo Calendar"
              allow="payment"
            />
          )}
        </div>
      </div>
    </div>
  )
}
